import jsPDF from "jspdf";
import { registerQuoteFonts } from "@/lib/pdfFont";
import { COMPANY } from "@/lib/company";
import { loadCompanyLogo, logoAspect } from "@/lib/branding";
import { loadIcons } from "@/lib/pdfIcons";

// Sade kurumsal belge iskeleti (Cari Defter, Cari/Tedarikçi Ekstresi, Tahsilat Makbuzu) — lacivert/altın, yazıcı dostu
export const NAVY = [15, 39, 69];
const NAVY_HEX = "#0F2745";
export const GOLD = [242, 183, 5];
export const INK = [30, 41, 59];
export const TEXT = [51, 65, 85];
export const SLATE = [100, 116, 139];
export const LINE = [222, 228, 236];
export const WASH = [245, 247, 250];
export const BAND = [240, 243, 248];
export const CREAM = [255, 248, 225];
export const WHITE = [255, 255, 255];
export const GREEN = [22, 128, 61];
export const RED = [185, 28, 28];
export const hexRgb = (h) => [parseInt(h.slice(1, 3), 16), parseInt(h.slice(3, 5), 16), parseInt(h.slice(5, 7), 16)];
export const money = (v) => `${new Intl.NumberFormat("tr-TR", { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(Number(v || 0))} ₺`;
export const titleTr = (s) => String(s || "").toLocaleLowerCase("tr").replace(/(^|[\s/,.\-(])(\S)/g, (m, a, b) => a + b.toLocaleUpperCase("tr"));
const BADGES = [["truck", "HIZLI", "TEDARİK"], ["box", "GENİŞ ÜRÜN", "YELPAZESİ"], ["shield", "ORİJİNAL", "ÜRÜNLER"], ["headset", "TEKNİK", "DESTEK"]];

export async function createDoc({ title, subtitle = "", contLine = "" }) {
  const [logo, icons] = await Promise.all([loadCompanyLogo(), loadIcons(NAVY_HEX)]);
  const AR = logoAspect();
  const pdf = new jsPDF({ orientation: "p", unit: "mm", format: "a4", compress: true });
  const F = await registerQuoteFonts(pdf);
  const pageW = pdf.internal.pageSize.getWidth();
  const pageH = pdf.internal.pageSize.getHeight();
  const M = 12, W = pageW - M * 2, FOOT = 12, HEAD1 = 60, HEAD2 = 22;
  const bottom = pageH - FOOT - 3;
  const T = (font, style, size, color) => { pdf.setFont(font, style); pdf.setFontSize(size); pdf.setTextColor(...color); };
  const fill = (c) => pdf.setFillColor(...c);
  const stroke = (c, w = 0.3) => { pdf.setDrawColor(...c); pdf.setLineWidth(w); };
  const icon = (name, x, y, s) => { if (icons[name]) { try { pdf.addImage(icons[name], "PNG", x, y, s, s, `ic-${name}`, "FAST"); } catch { /* noop */ } } };
  const image = (data, x, y, w, h) => { try { pdf.addImage(data, "PNG", x, y, w, h, undefined, "FAST"); return true; } catch { return false; } };
  const shrink = (text, maxW, font, style, size, min) => { let fs = size; T(font, style, fs, INK); while (pdf.getTextWidth(text) > maxW && fs > min) { fs -= 0.3; pdf.setFontSize(fs); } return fs; };
  const ellipsize = (text, maxW) => { let s = String(text || ""); if (pdf.getTextWidth(s) <= maxW) return s; while (s.length > 1 && pdf.getTextWidth(`${s}…`) > maxW) s = s.slice(0, -1); return `${s.trimEnd()}…`; };
  const drawn = new Set();

  // Başlık bloğu: yazı, altın çizgi ve alt başlık AYNI genişlikte. charSpace sağa hizalamayı kaydırdığı için x elle hesaplanır.
  const titleBlock = (text, sub, ty, maxW, maxSize, minSize) => {
    const csOf = (s) => s * 0.02;
    let fs = maxSize;
    T(F.head, "bold", fs, NAVY);
    while (pdf.getTextWidth(text) + csOf(fs) * (text.length - 1) > maxW && fs > minSize) { fs -= 0.3; pdf.setFontSize(fs); }
    const cs = csOf(fs);
    const tw = pdf.getTextWidth(text) + cs * (text.length - 1);
    const x = pageW - M - tw;
    pdf.text(text, x, ty, { charSpace: cs });
    const barY = ty + fs * 0.3528 * 0.22, barH = Math.max(0.7, fs * 0.045);
    fill(GOLD); pdf.rect(x, barY, tw, barH, "F");
    if (!sub) return barY + barH;
    let sfs = Math.max(6.5, fs * 0.36);
    T(F.body, "bold", sfs, SLATE);
    while (pdf.getTextWidth(sub) > tw && sfs > 5) { sfs -= 0.2; pdf.setFontSize(sfs); }
    const scs = sub.length > 1 ? Math.max(0, (tw - pdf.getTextWidth(sub)) / (sub.length - 1)) : 0;
    const sy = barY + barH + sfs * 0.3528 * 0.72 + 2;
    pdf.text(sub, x, sy, { charSpace: scs });
    return sy;
  };

  const footer = () => {
    fill(GOLD); pdf.rect(M, pageH - FOOT + 0.6, W, 0.5, "F");
    T(F.head, "normal", 6.4, NAVY); pdf.text(COMPANY.name, M, pageH - FOOT + 5.4);
    T(F.body, "normal", 5.6, SLATE); pdf.text(titleTr(COMPANY.address), M, pageH - FOOT + 8.7);
    T(F.body, "bold", 6, NAVY); pdf.text(`${COMPANY.email}   ·   ${COMPANY.phone}`, pageW - M, pageH - FOOT + 5.4, { align: "right" });
  };

  const header = (pageNo) => {
    if (drawn.has(pageNo)) return;
    drawn.add(pageNo);
    if (pageNo === 1) {
      const lh = 19, lw = Math.min(lh * AR, 84);
      if (!logo || !image(logo, M, 8, lw, lh)) { T(F.head, "bold", 18, NAVY); pdf.text(COMPANY.name, M, 8 + lh * 0.65); }
      const infoFs = 7.8, step = infoFs * 0.5, availW = 112 - M - 7.6;
      let iy = 8 + lh + 7.5;
      [["pin", titleTr(COMPANY.address)], ["phone", `${COMPANY.phone}   |   ${COMPANY.salesRep}`], ["mail", COMPANY.email], ["building", `Vergi Dairesi: ${titleTr(COMPANY.taxOffice)}   ·   TCKN: ${COMPANY.tckn}`]].forEach(([ic, txt]) => {
        T(F.body, "normal", infoFs, INK);
        const lines = pdf.splitTextToSize(txt, availW).slice(0, 2);
        icon(ic, M + 0.2, iy - 3.3, 4);
        pdf.text(lines, M + 7, iy, { lineHeightFactor: 1.4 });
        iy += lines.length * step + 1.5;
      });
      titleBlock(title, subtitle, 26, 78, 26, 15);
      fill(GOLD); pdf.rect(0, HEAD1, pageW, 0.8, "F");
    } else {
      const lh = 10, lw = Math.min(lh * AR, 42);
      if (logo) image(logo, M, 5, lw, lh);
      titleBlock(title, "", 11, 90, 12.5, 9);
      if (contLine) { T(F.body, "normal", 6.3, SLATE); pdf.text(ellipsize(contLine, 125), pageW - M, 17.2, { align: "right" }); }
      fill(GOLD); pdf.rect(M, HEAD2 - 1.6, W, 0.8, "F");
    }
    footer();
  };

  const ensure = (y, need) => {
    if (y + need <= bottom) return y;
    pdf.addPage(); header(pdf.getNumberOfPages());
    return HEAD2 + 7;
  };

  // Taraf kartı: solda etiket + ünvan + ek satırlar; sağda etiket/değer satırları. Yüksekliği döner.
  const partyCard = (y0, { label = "SAYIN", name = "", lines = [], meta = [] }) => {
    const leftW = 120, nameW = leftW - 10;
    let nfs = 11.5, nameLines = [name || "—"];
    for (const fs of [11.5, 10.5, 9.5, 8.8]) { nfs = fs; T(F.head, "bold", fs, NAVY); nameLines = pdf.splitTextToSize(name || "—", nameW); if (nameLines.length <= 2) break; }
    nameLines = nameLines.slice(0, 3);
    const lhN = nfs * 0.3528 * 1.15;
    T(F.body, "normal", 6.6, SLATE);
    const metaLines = lines.filter(Boolean).map((s) => ellipsize(s, nameW));
    const leftH = 8.5 + nameLines.length * lhN + (metaLines.length ? 1.5 + metaLines.length * 3.5 : 0) + 2.5;
    const rowH = Math.max(21, leftH, meta.length * 8 + 4);
    stroke(LINE, 0.35); fill(WHITE); pdf.roundedRect(M, y0, W, rowH, 2, 2, "FD");
    fill(GOLD); pdf.rect(M, y0 + 3, 1.1, rowH - 6, "F");
    T(F.body, "bold", 6, SLATE); pdf.text(label, M + 5, y0 + 5.8, { charSpace: 0.8 });
    const ny = y0 + 5.8 + lhN + 0.4;
    T(F.head, "bold", nfs, NAVY); pdf.text(nameLines, M + 5, ny, { lineHeightFactor: 1.15 });
    let my = ny + lhN * (nameLines.length - 1) + 5;
    T(F.body, "normal", 6.6, SLATE); metaLines.forEach((s) => { pdf.text(s, M + 5, my); my += 3.5; });
    if (meta.length) {
      const rx = M + leftW + 6;
      stroke(LINE, 0.35); pdf.line(rx - 6, y0 + 3.5, rx - 6, y0 + rowH - 3.5);
      const top = y0 + (rowH - (meta.length * 8 - 1)) / 2;
      meta.forEach(([lab, val], i) => {
        const ly = top + 3 + i * 8;
        T(F.body, "bold", 5.8, SLATE); pdf.text(lab, rx, ly, { charSpace: 0.5 });
        const v = String(val);
        const vfs = shrink(v, pageW - M - 4 - rx, F.head, "bold", 9.4, 6.5);
        T(F.head, "bold", vfs, NAVY); pdf.text(v, rx, ly + 4);
      });
    }
    return rowH;
  };

  const sectionTitle = (y, label, right) => {
    T(F.head, "bold", 8.8, NAVY); pdf.text(label, M, y, { charSpace: 0.4 });
    fill(GOLD); pdf.rect(M, y + 1.6, 9, 0.7, "F");
    if (right) { T(F.body, "normal", 6.8, SLATE); pdf.text(right, pageW - M, y, { align: "right" }); }
    return y + 4.5;
  };

  // Sağda sade toplamlar + lacivert bakiye kutusu. rows: [etiket, değer, kalın?, renk?]
  const totalsHeight = (n) => n * 7 + 2 + 15;
  const totalsBox = (y, rows, open, L = {}) => {
    const lab = { pos: "KALAN BAKİYE", posSub: "ÖDENMESİ GEREKEN TUTAR", neg: "ALACAKLI BAKİYE", negSub: "FAZLA ÖDEME", ...L };
    const rightW = 82, rx = M + W - rightW, rh = 7, balH = 15;
    let ty = y;
    rows.forEach(([label, val, bold, col]) => {
      T(F.body, bold ? "bold" : "normal", 8, INK); pdf.text(label, rx + 2, ty + 4.8, { charSpace: 0.2 });
      T(F.body, "bold", bold ? 9.2 : 8.6, col || NAVY); pdf.text(val, rx + rightW - 2, ty + 4.8, { align: "right" });
      stroke(LINE, 0.3); pdf.line(rx, ty + rh, rx + rightW, ty + rh);
      ty += rh;
    });
    ty += 2;
    const balLabel = open > 0.005 ? lab.pos : open < -0.005 ? lab.neg : "HESAP KAPALI";
    const balSub = open > 0.005 ? lab.posSub : open < -0.005 ? lab.negSub : "BAKİYE YOK";
    fill(NAVY); pdf.roundedRect(rx, ty, rightW, balH, 2, 2, "F");
    T(F.head, "bold", 8.6, WHITE); pdf.text(balLabel, rx + 4, ty + 6.4);
    T(F.body, "bold", 5.4, [203, 213, 225]); pdf.text(balSub, rx + 4, ty + 11, { charSpace: 0.3 });
    T(F.head, "bold", 8.6, WHITE);
    const amt = money(Math.abs(open));
    const afs = shrink(amt, rightW - 12 - pdf.getTextWidth(balLabel), F.head, "bold", 13, 7.5);
    T(F.head, "bold", afs, GOLD); pdf.text(amt, rx + rightW - 4, ty + balH / 2 + 2, { align: "right" });
    return ty + balH - y;
  };

  // Ortak tablo görünümü (lacivert başlık, ince ızgara); başlık ayraçları + "ana satır + alt satırlar" hücresi
  const tableBase = (extra = {}) => ({
    theme: "grid", rowPageBreak: "avoid", margin: { left: M, right: M, top: HEAD2 + 5, bottom: FOOT + 5 },
    styles: { font: F.body, fontSize: 7.6, cellPadding: { top: 1.4, bottom: 1.4, left: 1.8, right: 1.8 }, textColor: INK, lineColor: LINE, lineWidth: 0.2, overflow: "linebreak", valign: "middle" },
    headStyles: { font: F.body, fontStyle: "bold", fillColor: NAVY, textColor: WHITE, fontSize: 6.8, valign: "middle", minCellHeight: 8, lineWidth: 0, cellPadding: { top: 1.6, bottom: 1.6, left: 1.4, right: 1.4 } },
    alternateRowStyles: { fillColor: [250, 251, 253] },
    didDrawPage: (d) => header(d.pageNumber),
    ...extra,
  });
  const cellHooks = (colIdx, base = 7.6, mainStyle = "bold", navyHead = true) => ({
    willDrawCell: (d) => {
      if (colIdx < 0 || d.section !== "body" || d.column.index !== colIdx || typeof d.cell.raw !== "string") return;
      const parts = d.cell.raw.split("\n"); d.cell._name = parts[0]; d.cell._subs = parts.slice(1); d.cell.text = [];
    },
    didDrawCell: (d) => {
      if (navyHead && d.section === "head" && d.column.index > 0) { fill([44, 68, 102]); pdf.rect(d.cell.x, d.cell.y + 2.2, 0.25, d.cell.height - 4.4, "F"); }
      if (d.cell._name == null) return;
      const { x, y, width, height } = d.cell; const padL = d.cell.padding("left"), avail = width - padL - d.cell.padding("right");
      const subFs = base - 1.3;
      T(F.body, mainStyle, base, INK); const nameLs = pdf.splitTextToSize(d.cell._name, avail);
      T(F.body, "normal", subFs, SLATE); const subLs = d.cell._subs.flatMap((s) => pdf.splitTextToSize(s, avail));
      const lh = base * 0.42, slh = subFs * 0.42;
      let ty = y + (height - (nameLs.length * lh + subLs.length * slh)) / 2 + lh * 0.78;
      T(F.body, mainStyle, base, INK); nameLs.forEach((ln) => { pdf.text(ln, x + padL, ty); ty += lh; });
      T(F.body, "normal", subFs, SLATE); subLs.forEach((ln) => { pdf.text(ln, x + padL, ty); ty += slh; });
    },
  });

  // Güven rozetleri — son sayfanın en altına yakın (içerik taşarsa yeni sayfa)
  const isz = 7, badgeH = isz + 7.6;
  const badgesAtBottom = (y) => {
    let by = Math.max(ensure(y, badgeH), bottom - badgeH);
    stroke(LINE, 0.4); pdf.line(M, by, pageW - M, by);
    by += 3.4;
    const cw = W / BADGES.length;
    T(F.head, "normal", 6.8, NAVY);
    BADGES.forEach(([ic, l1, l2], i) => {
      const bx = M + i * cw;
      const textW = Math.max(pdf.getTextWidth(l1), pdf.getTextWidth(l2));
      const sx = bx + (cw - (isz + 2.6 + textW)) / 2;
      icon(ic, sx, by, isz);
      pdf.text(l1, sx + isz + 2.6, by + isz * 0.42); pdf.text(l2, sx + isz + 2.6, by + isz * 0.86);
      if (i > 0) { stroke(LINE, 0.35); pdf.line(bx, by + 0.4, bx, by + isz - 0.4); }
    });
  };

  const finish = () => {
    const n = pdf.getNumberOfPages();
    for (let i = 1; i <= n; i++) {
      pdf.setPage(i);
      T(F.body, "normal", 6, SLATE); pdf.text(`Sayfa ${i} / ${n}`, pageW - M, pageH - FOOT + 8.7, { align: "right" });
    }
    return pdf;
  };

  return { pdf, F, T, fill, stroke, icon, shrink, ellipsize, pageW, pageH, M, W, FOOT, HEAD1, HEAD2, bottom, header, ensure, partyCard, sectionTitle, totalsHeight, totalsBox, tableBase, cellHooks, badgesAtBottom, finish };
}

export const partyLines = (c = {}) => [
  [c.tax_no ? `VKN/TCKN: ${c.tax_no}` : "", c.tax_office ? `V.D.: ${c.tax_office}` : "", c.phone ? `Tel: ${c.phone}` : ""].filter(Boolean).join("   ·   "),
  [c.address, c.district, c.city].filter(Boolean).join(" · "),
];
